import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SupportChatApi} from './supportChatApi';
import {SupportComposer} from './SupportComposer';
import {SupportAvatar} from './SupportAvatar';
import {SupportMessageText} from './SupportMessageText';
import {displayNameFor} from './supportTeamIdentity';
import {Spinner} from '../Spinner';
import {dayKey, dayLabelParts, formatTimeShort} from './supportChatTime';

// SupportConversationThread — un hilo de conversación abierto.
//
// Props:
//   conversationId, conversationStatus ('OPEN'|'CLOSED'), conversationImported (bool),
//   onStatusChange(status), onActivity(), onStartNew() — navegar a "nueva conversación",
//   onLoadError() — la carga inicial falló (p. ej. una conversación que no es accesible).
// Ref (imperativo): refetchIncremental() — lo llama el panel ante una señal de realtime del hilo
//   abierto (o el polling de fallback).
//
// Comportamiento:
//   - Carga inicial: fetchMessages (últimos 50 asc) + markRead.
//   - Cuando llegan mensajes nuevos con el hilo visible → markRead de nuevo.
//   - "Cargar mensajes anteriores" (has_more_before) → prepend manteniendo scroll.
//   - Auto-scroll al fondo en carga inicial y ante mensaje nuevo si el usuario ya estaba cerca del fondo.
//   - Conversación importada de Intercom (imported_from_intercom): solo lectura. Sin composer,
//     sin banner de reabrir (la importada es cerrada pero NO reabrible) — se muestra un footer
//     sutil con CTA a "nueva conversación".

const NEAR_BOTTOM_PX = 80;

// El estado de la conversación: el GET de mensajes lo manda adentro de `conversation`
// (`{conversation: {status}}`), y el POST de un mensaje, como `conversation_status`.
function statusFrom(data) {
    return data.conversation_status || (data.conversation && data.conversation.status) || null;
}

const SupportConversationThreadInner = ({conversationId, conversationStatus, conversationImported, onStatusChange, onActivity, onStartNew, onLoadError}, ref) => {
    const {t} = useTranslation();

    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMoreBefore, setHasMoreBefore] = useState(false);
    const [status, setStatus] = useState(conversationStatus || 'OPEN');
    // Read-only por importación de Intercom. Semilla desde el shape de la lista; el GET messages
    // puede confirmarlo (tolerar undefined → false: el backend puede deployarse después que el FE).
    const [imported, setImported] = useState(!!conversationImported);

    const scrollRef = useRef(null);
    const messagesRef = useRef([]); // espejo para leer sin cerrar sobre state viejo.

    const syncMessages = useCallback((next) => {
        messagesRef.current = next;
        setMessages(next);
    }, []);

    const isNearBottom = () => {
        const el = scrollRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    };

    const scrollToBottom = useCallback(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, []);

    const markReadSilently = useCallback(() => {
        SupportChatApi.markRead(conversationId).then(() => {
            if (typeof onActivity === 'function') onActivity();
        });
    }, [conversationId, onActivity]);

    // ── Carga inicial ──────────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        SupportChatApi.fetchMessages(conversationId, {limit: 50}).then((data) => {
            if (cancelled) return;
            if (data && data.status === 'OK') {
                const list = data.messages || [];
                syncMessages(list);
                setHasMoreBefore(!!data.has_more_before);
                const loadedStatus = statusFrom(data);
                if (loadedStatus) {
                    setStatus(loadedStatus);
                    if (typeof onStatusChange === 'function') onStatusChange(loadedStatus);
                }
                // El objeto `conversation` del GET messages puede traer imported_from_intercom.
                // Tolerar undefined → conservamos el valor sembrado desde la lista (no lo pisamos).
                const importedFlag = data.conversation && data.conversation.imported_from_intercom;
                if (importedFlag !== undefined) setImported(!!importedFlag);
                markReadSilently();
            } else if (typeof onLoadError === 'function') {
                onLoadError();
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    // Auto-scroll al fondo tras la carga inicial.
    useEffect(() => {
        if (!loading) scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // ── Refetch incremental (realtime / polling) ────────────────────────────────
    const refetchIncremental = useCallback(() => {
        const current = messagesRef.current;
        const last = current.length ? current[current.length - 1] : null;
        const after = last ? last.created_at : undefined;
        const stickToBottom = isNearBottom();
        SupportChatApi.fetchMessages(conversationId, after ? {after} : {limit: 50}).then((data) => {
            if (!data || data.status !== 'OK') return;
            const incoming = data.messages || [];
            const fetchedStatus = statusFrom(data);
            if (fetchedStatus && fetchedStatus !== status) {
                setStatus(fetchedStatus);
                if (typeof onStatusChange === 'function') onStatusChange(fetchedStatus);
            }
            if (incoming.length === 0) return;
            // Dedup por id (por si el after solapa un mensaje ya presente).
            const knownIds = new Set(current.map((m) => m.id));
            const fresh = incoming.filter((m) => !knownIds.has(m.id));
            if (fresh.length === 0) return;
            const merged = current.concat(fresh);
            syncMessages(merged);
            markReadSilently();
            if (stickToBottom) {
                // Esperar al paint para scrollear al fondo con el contenido nuevo.
                requestAnimationFrame(scrollToBottom);
            }
        });
    }, [conversationId, status, onStatusChange, syncMessages, markReadSilently, scrollToBottom]);

    useImperativeHandle(ref, () => ({refetchIncremental}), [refetchIncremental]);

    // ── Cargar mensajes anteriores (prepend manteniendo scroll) ─────────────────
    const loadMore = () => {
        if (loadingMore || !hasMoreBefore) return;
        const current = messagesRef.current;
        const first = current.length ? current[0] : null;
        if (!first) return;
        setLoadingMore(true);
        const el = scrollRef.current;
        const prevScrollHeight = el ? el.scrollHeight : 0;
        SupportChatApi.fetchMessages(conversationId, {beforeId: first.id, limit: 50}).then((data) => {
            if (data && data.status === 'OK') {
                const older = data.messages || [];
                if (older.length > 0) {
                    const knownIds = new Set(current.map((m) => m.id));
                    const fresh = older.filter((m) => !knownIds.has(m.id));
                    syncMessages(fresh.concat(current));
                    // Mantener la posición de scroll: compensar el alto agregado arriba.
                    requestAnimationFrame(() => {
                        const el2 = scrollRef.current;
                        if (el2) el2.scrollTop = el2.scrollHeight - prevScrollHeight;
                    });
                }
                setHasMoreBefore(!!data.has_more_before);
            }
            setLoadingMore(false);
        });
    };

    // ── Envío desde el composer ─────────────────────────────────────────────────
    const handleSend = (message, attachmentBlob) => {
        return SupportChatApi.postMessage(conversationId, message, attachmentBlob);
    };

    const handleSent = (response) => {
        // El backend devuelve el mensaje creado (y el nuevo estado si reabrió).
        if (response.message) {
            const current = messagesRef.current;
            if (!current.some((m) => m.id === response.message.id)) {
                syncMessages(current.concat([response.message]));
            }
        }
        if (response.conversation_status && response.conversation_status !== status) {
            setStatus(response.conversation_status);
            if (typeof onStatusChange === 'function') onStatusChange(response.conversation_status);
        }
        if (typeof onActivity === 'function') onActivity();
        requestAnimationFrame(scrollToBottom);
    };

    const renderMessages = () => {
        const nodes = [];
        let lastDay = null;
        messages.forEach((msg) => {
            const key = dayKey(msg.created_at);
            if (key && key !== lastDay) {
                lastDay = key;
                nodes.push(renderDaySeparator(msg.created_at, key));
            }
            nodes.push(renderMessage(msg));
        });
        return nodes;
    };

    const renderDaySeparator = (createdAt, key) => {
        const parts = dayLabelParts(createdAt);
        let label;
        if (parts.kind === 'today') label = t('support_chat.day_today');
        else if (parts.kind === 'yesterday') label = t('support_chat.day_yesterday');
        else label = parts.label;
        return (
            <div className="support-chat-thread__day" key={`day-${key}`}>
                <span>{label}</span>
            </div>
        );
    };

    const renderMessage = (msg) => {
        const senderType = msg.author_type; // 'ADMIN' | 'CLIENT' | 'SYSTEM' (contrato del backend)
        if (senderType === 'SYSTEM') {
            return (
                <div className="support-chat-msg -system" key={msg.id}>
                    <span>{msg.body}</span>
                </div>
            );
        }

        const isMine = !!msg.is_mine;
        const isTeam = senderType === 'ADMIN';
        // Mis mensajes y los de colegas de mi agencia van a la derecha. Los del equipo, a la izq.
        const side = isTeam ? '-left' : '-right';
        // Nombre arriba: equipo → "Nombre · Dashbo"; colega (no yo) → su nombre.
        const showName = isTeam || (!isMine && senderType === 'CLIENT');
        const nameLabel = isTeam ? displayNameFor(msg.author_name) : (msg.author_name || '');

        return (
            <div className={`support-chat-msg ${side} ${isTeam ? '-team' : '-client'}`} key={msg.id}>
                {isTeam && (
                    <div className="support-chat-msg__avatar">
                        <SupportAvatar name={msg.author_name} size={28} avatarUrl={msg.author_avatar_url} team/>
                    </div>
                )}
                <div className="support-chat-msg__content">
                    {showName && <span className="support-chat-msg__name">{nameLabel}</span>}
                    <div className="support-chat-msg__bubble">
                        {msg.body && <SupportMessageText body={msg.body}/>}
                        {msg.attachment_url && (
                            <a
                                href={msg.attachment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="support-chat-msg__attachment"
                            >
                                <img src={msg.attachment_url} alt="" loading="lazy"/>
                            </a>
                        )}
                    </div>
                    <span className="support-chat-msg__time">{formatTimeShort(msg.created_at)}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="support-chat-thread">
            <div className="support-chat-thread__scroll" ref={scrollRef}>
                {loading ? (
                    <div className="box-loader"><Spinner color="positive"/></div>
                ) : (
                    <>
                        {hasMoreBefore && (
                            <div className="support-chat-thread__load-more">
                                <button type="button" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? t('support_chat.loading') : t('support_chat.load_previous')}
                                </button>
                            </div>
                        )}
                        {renderMessages()}
                    </>
                )}
            </div>

            {imported ? (
                // Importada de Intercom → solo lectura. Sin composer y sin banner de reabrir.
                <div className="support-chat-thread__readonly">
                    <span className="support-chat-thread__readonly-text">
                        {t('support_chat.imported_read_only')}
                    </span>
                    {typeof onStartNew === 'function' && (
                        <button
                            type="button"
                            className="support-chat-thread__readonly-cta"
                            onClick={onStartNew}
                        >
                            {t('support_chat.imported_start_new')}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {status === 'CLOSED' && (
                        <div className="support-chat-thread__closed-banner">{t('support_chat.closed_reopen')}</div>
                    )}

                    <SupportComposer
                        draftKey={conversationId}
                        onSend={handleSend}
                        onSent={handleSent}
                    />
                </>
            )}
        </div>
    );
};

export const SupportConversationThread = forwardRef(SupportConversationThreadInner);
